'use client';

import React, { useState } from 'react';
import { ArrowLeft, User, Bell, Volume2, Shield, Database, BellOff, VolumeX, ChevronRight, Trash2, Download, Lock, Eye, EyeOff, Smartphone, LogOut, Check,  } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';

interface SettingsScreenProps {
  onBack: () => void;
}

export default function SettingsScreen({ onBack }: SettingsScreenProps) {
  const { signOut } = useAuth();
  const router = useRouter();
  const [notifications, setNotifications] = useState(true);
  const [messagePreview, setMessagePreview] = useState(true);
  const [sound, setSound] = useState(true);
  const [vibration, setVibration] = useState(true);
  const [readReceipts, setReadReceipts] = useState(true);
  const [onlineStatus, setOnlineStatus] = useState(true);
  const [lastSeen, setLastSeen] = useState(true);
  const [savedToast, setSavedToast] = useState(false);

  const handleSave = () => {
    setSavedToast(true);
    setTimeout(() => setSavedToast(false), 2000);
  };

  return (
    <div className="flex flex-col h-screen w-full overflow-hidden" style={{ background: 'var(--background)' }}>
      {/* Header */}
      <header
        className="chat-header-surface flex items-center gap-2.5 px-2 sm:px-3 flex-shrink-0 z-20 relative"
        style={{ height: '64px', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}
      >
        <button
          onClick={onBack}
          className="flex items-center justify-center w-[44px] h-[44px] rounded-full hover:bg-white/8 transition-colors duration-150 active:scale-95 flex-shrink-0"
          aria-label="Go back"
        >
          <ArrowLeft size={22} style={{ color: 'var(--foreground)' }} />
        </button>
        <h1 className="text-[17px] font-semibold tracking-[0.01em] flex-1" style={{ color: 'var(--foreground)' }}>Settings</h1>
        <button
          onClick={handleSave}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[13px] font-semibold transition-all duration-150 active:scale-95"
          style={{ background: 'var(--primary)', color: '#fff' }}
        >
          {savedToast ? <Check size={14} /> : null}
          {savedToast ? 'Saved' : 'Save'}
        </button>
      </header>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(128,128,128,0.2) transparent', WebkitOverflowScrolling: 'touch' }}>
        <div className="px-4 py-4 space-y-3 pb-8">

          {/* ── ACCOUNT ── */}
          <SectionLabel icon={<User size={13} />}>Account</SectionLabel>
          <InfoCard>
            {/* Profile row */}
            <div className="flex items-center gap-3 px-4 py-4">
              <div
                className="w-[52px] h-[52px] rounded-full flex items-center justify-center text-white font-bold text-[18px] flex-shrink-0"
                style={{ background: 'linear-gradient(135deg, #287FD8 0%, #6650D8 100%)' }}
              >
                AB
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[15px] font-semibold truncate" style={{ color: 'var(--foreground)' }}>Ate Boni</p>
                <p className="text-[12px] mt-0.5" style={{ color: 'var(--muted-foreground)' }}>+63 912 345 6789</p>
              </div>
              <ChevronRight size={16} style={{ color: 'var(--muted-foreground)' }} />
            </div>
            <Divider />
            <ActionRow
              icon={<Smartphone size={16} style={{ color: 'var(--primary)' }} />}
              label="Linked Devices"
              description="Manage connected devices"
              chevron
            />
            <Divider />
            <ActionRow
              icon={<Lock size={16} style={{ color: 'var(--primary)' }} />}
              label="Change Passphrase"
              description="Update your security passphrase"
              chevron
            />
          </InfoCard>

          {/* ── NOTIFICATIONS ── */}
          <SectionLabel icon={<Bell size={13} />}>Notifications</SectionLabel>
          <InfoCard>
            <ToggleRow
              icon={notifications
                ? <Bell size={18} style={{ color: 'var(--primary)' }} />
                : <BellOff size={18} style={{ color: '#91A3B7' }} />}
              label="Push Notifications"
              description={notifications ? 'Receive message alerts' : 'Notifications are off'}
              enabled={notifications}
              onToggle={() => setNotifications(v => !v)}
            />
            <Divider />
            <ToggleRow
              icon={<Eye size={18} style={{ color: messagePreview ? 'var(--primary)' : '#91A3B7' }} />}
              label="Message Preview"
              description={messagePreview ? 'Show content in notifications' : 'Hide message content'}
              enabled={messagePreview}
              onToggle={() => setMessagePreview(v => !v)}
            />
          </InfoCard>

          {/* ── SOUND ── */}
          <SectionLabel icon={<Volume2 size={13} />}>Sound & Vibration</SectionLabel>
          <InfoCard>
            <ToggleRow
              icon={sound
                ? <Volume2 size={18} style={{ color: 'var(--primary)' }} />
                : <VolumeX size={18} style={{ color: '#91A3B7' }} />}
              label="Message Sounds"
              description={sound ? 'Play sound for new messages' : 'Sound is muted'}
              enabled={sound}
              onToggle={() => setSound(v => !v)}
            />
            <Divider />
            <ToggleRow
              icon={<Smartphone size={18} style={{ color: vibration ? 'var(--primary)' : '#91A3B7' }} />}
              label="Vibration"
              description={vibration ? 'Vibrate on new messages' : 'Vibration is off'}
              enabled={vibration}
              onToggle={() => setVibration(v => !v)}
            />
          </InfoCard>

          {/* ── PRIVACY ── */}
          <SectionLabel icon={<Shield size={13} />}>Privacy</SectionLabel>
          <InfoCard>
            <ToggleRow
              icon={<Check size={18} style={{ color: readReceipts ? 'var(--primary)' : '#91A3B7' }} />}
              label="Read Receipts"
              description={readReceipts ? 'Others can see when you read' : 'Read receipts hidden'}
              enabled={readReceipts}
              onToggle={() => setReadReceipts(v => !v)}
            />
            <Divider />
            <ToggleRow
              icon={onlineStatus
                ? <Eye size={18} style={{ color: 'var(--primary)' }} />
                : <EyeOff size={18} style={{ color: '#91A3B7' }} />}
              label="Online Status"
              description={onlineStatus ? 'Others can see you\'re online' : 'Online status hidden'}
              enabled={onlineStatus}
              onToggle={() => setOnlineStatus(v => !v)}
            />
            <Divider />
            <ToggleRow
              icon={lastSeen
                ? <Eye size={18} style={{ color: 'var(--primary)' }} />
                : <EyeOff size={18} style={{ color: '#91A3B7' }} />}
              label="Last Seen"
              description={lastSeen ? 'Show last seen timestamp' : 'Last seen hidden'}
              enabled={lastSeen}
              onToggle={() => setLastSeen(v => !v)}
            />
            <Divider />
            <ActionRow
              icon={<Lock size={16} style={{ color: 'var(--primary)' }} />}
              label="Blocked Contacts"
              description="Manage blocked users"
              chevron
            />
          </InfoCard>

          {/* ── DATA MANAGEMENT ── */}
          <SectionLabel icon={<Database size={13} />}>Data Management</SectionLabel>
          <InfoCard>
            <ActionRow
              icon={<Download size={16} style={{ color: 'var(--primary)' }} />}
              label="Export Chat History"
              description="Download all your messages"
              chevron
            />
            <Divider />
            <ActionRow
              icon={<Database size={16} style={{ color: 'var(--primary)' }} />}
              label="Storage Usage"
              description="Manage cached media and files"
              chevron
            />
            <Divider />
            <ActionRow
              icon={<Trash2 size={16} className="text-red-400" />}
              label="Clear All Chats"
              description="Delete all messages permanently"
              danger
            />
          </InfoCard>

          {/* Sign out */}
          <InfoCard>
            <button className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-red-500/10 transition-colors duration-150 active:bg-red-500/15" onClick={async () => { await signOut(); }}>
              <span
                className="w-[32px] h-[32px] rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: 'rgba(239,68,68,0.12)' }}
              >
                <LogOut size={16} className="text-red-400" />
              </span>
              <div className="flex-1 text-left">
                <p className="text-[14px] font-medium text-red-400">Sign Out</p>
                <p className="text-[12px] mt-0.5" style={{ color: 'var(--muted-foreground)' }}>Log out of your account</p>
              </div>
            </button>
          </InfoCard>

          <div className="h-4" />
        </div>
      </div>
    </div>
  );
}

/* ── Sub-components ── */

function InfoCard({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
    >
      {children}
    </div>
  );
}

function SectionLabel({ children, icon }: { children: React.ReactNode; icon?: React.ReactNode }) {
  return (
    <div className="flex items-center gap-1.5 px-1 pt-1">
      {icon && <span style={{ color: '#91A3B7' }}>{icon}</span>}
      <p className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: '#91A3B7' }}>
        {children}
      </p>
    </div>
  );
}

function Divider() {
  return <div className="mx-4" style={{ height: '1px', background: 'var(--border)' }} />;
}

function ToggleRow({
  icon, label, description, enabled, onToggle,
}: {
  icon: React.ReactNode;
  label: string;
  description: string;
  enabled: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="flex items-center gap-3 px-4 py-3.5">
      <span
        className="w-[32px] h-[32px] rounded-full flex items-center justify-center flex-shrink-0"
        style={{ background: 'var(--border)' }}
      >
        {icon}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-[14px] font-medium" style={{ color: 'var(--foreground)' }}>{label}</p>
        <p className="text-[12px] mt-0.5" style={{ color: 'var(--muted-foreground)' }}>{description}</p>
      </div>
      <button
        onClick={onToggle}
        role="switch"
        aria-checked={enabled}
        aria-label={`Toggle ${label}`}
        className="flex-shrink-0 w-[51px] h-[28px] rounded-full relative transition-colors duration-200 focus:outline-none"
        style={{ background: enabled ? 'var(--primary)' : 'var(--border)' }}
      >
        <span
          className="absolute top-[4px] w-[20px] h-[20px] rounded-full bg-white shadow-md transition-all duration-200"
          style={{ left: enabled ? '27px' : '4px' }}
        />
      </button>
    </div>
  );
}

function ActionRow({
  icon, label, description, chevron, danger,
}: {
  icon: React.ReactNode;
  label: string;
  description: string;
  chevron?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      className={`w-full flex items-center gap-3 px-4 py-3.5 text-left transition-colors duration-150 ${
        danger ? 'hover:bg-red-500/10 active:bg-red-500/15' : 'hover:bg-black/5 active:bg-black/8'
      }`}
    >
      <span
        className="w-[32px] h-[32px] rounded-full flex items-center justify-center flex-shrink-0"
        style={{ background: danger ? 'rgba(239,68,68,0.12)' : 'var(--border)' }}
      >
        {icon}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-[14px] font-medium" style={{ color: danger ? '#f87171' : 'var(--foreground)' }}>{label}</p>
        <p className="text-[12px] mt-0.5" style={{ color: 'var(--muted-foreground)' }}>{description}</p>
      </div>
      {chevron && <ChevronRight size={16} style={{ color: 'var(--muted-foreground)' }} />}
    </button>
  );
}
